import { useState, useEffect, useRef } from "react"
import type { FC } from "react"
import { useCreatePost } from "../../hooks/usePosts"
import postsApi from "../../../../services/postsApi"
import type { Tag } from "../../models/posts"

interface NewPostFormProps {
  type: number
  refresh: () => void
  repost?: number
  repostId?: number
}

const NewPostForm: FC<NewPostFormProps> = ({ type, refresh, repost = 0, repostId }) => {
  const [content, setContent] = useState("")
  const [images, setImages] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [tags, setTags] = useState<Tag[]>([])
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [preview, setPreview] = useState<{ image: string; title: string; description: string; url: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [serverError, setServerError] = useState("")
  const fileRef = useRef<HTMLInputElement>(null)
  const createPostMutation = useCreatePost()

  useEffect(() => {
    postsApi.getTags(type).then(setAllTags)
  }, [type])

  const addTag = (tagId: string) => {
    if (!tagId) return
    const tag = allTags.find((t) => t.id === Number(tagId))
    if (tag && !tags.find((t) => t.id === tag.id)) {
      setTags([...tags, tag])
    }
  }

  const removeTag = (tagId: number) => {
    setTags(tags.filter((t) => t.id !== tagId))
  }

  const handleContentChange = (val: string) => {
    setContent(val)
    const urlMatch = /(https?:\/\/[^\s]+)/.exec(val)
    if (urlMatch) {
      fetch(`https://api.linkpreview.net/?key=5a02fa07ebddc33f6c40df1060aa3c548ad2d499eced0&q=${encodeURIComponent(urlMatch[0])}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.image) setPreview(data)
        })
        .catch(() => {})
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setImages([...images, ...files])
    setImagePreviews([...imagePreviews, ...files.map((f) => URL.createObjectURL(f))])
  }

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index))
    setImagePreviews(imagePreviews.filter((_, i) => i !== index))
  }

  const handleSubmit = () => {
    const errs: Record<string, string> = {}
    if (!content.trim() && images.length === 0) errs.content = "Preenchimento obrigatório!"
    if (tags.length === 0) errs.tags = "É obrigatório atribuir pelo menos uma tag!"

    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    setServerError("")
    setSubmitting(true)
    const formData = new FormData()
    formData.append("visible_to", String(type))
    formData.append("content", content)
    for (const t of tags) formData.append("tags[]", String(t.id))
    for (const img of images) formData.append("images[]", img)
    if (preview) {
      formData.append("preview_img", preview.image)
      formData.append("preview_title", preview.title)
      formData.append("preview_description", preview.description)
      formData.append("preview_url", preview.url)
    }
    if (repost === 1 && repostId) {
      formData.append("repost", "1")
      formData.append("post_id", String(repostId))
    }

    createPostMutation.mutate(formData, {
      onSuccess: (data) => {
        if (data.status === "success") {
          setContent("")
          setImages([])
          setImagePreviews([])
          setTags([])
          setPreview(null)
          setErrors({})
          refresh()
        } else {
          setErrors(data.errors || {})
        }
        setSubmitting(false)
      },
      onError: () => {
        setServerError("Ocorreu um erro ao criar o post. Tenta novamente.")
        setSubmitting(false)
      },
    })
  }

  return (
    <div className="bg-white rounded-lg mb-4 border border-gray-200">
      <div className="p-4">
        <textarea
          className="w-full border-0 resize-none focus:outline-none text-sm min-h-[80px] placeholder-gray-400"
          placeholder="Escreva aqui o seu post!"
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
        />
        {errors.content && <span className="text-xs text-red-500">{errors.content}</span>}

        {preview && (
          <div className="border rounded overflow-hidden mb-3">
            <img src={preview.image} alt="" className="w-full h-auto max-h-40 object-cover" />
            <div className="p-2 text-xs font-medium text-gray-700 bg-gray-50">{preview.title}</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-3">
          <select
            className="border border-gray-200 rounded px-2 py-1 text-xs"
            onChange={(e) => addTag(e.target.value)}
            value=""
          >
            <option value="">-- Tags --</option>
            {allTags
              .filter((t) => !tags.find((at) => at.id === t.id))
              .map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
          </select>
          {tags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full"
              onClick={() => removeTag(tag.id)}
            >
              {tag.name} <i className="fa fa-times ml-1" />
            </button>
          ))}
        </div>
        {errors.tags && <span className="text-xs text-red-500">{errors.tags}</span>}
        {serverError && (
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">
            <span className="text-xs text-red-600">{serverError}</span>
            <button type="button" className="text-red-400 hover:text-red-600 text-xs" onClick={() => setServerError("")}>
              <i className="fa fa-times" />
            </button>
          </div>
        )}

        {imagePreviews.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {imagePreviews.map((img) => (
              <div key={img} className="relative w-20 h-20">
                <img src={img} alt="" className="w-full h-full object-cover rounded" />
                <button
                  type="button"
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-xs flex items-center justify-center"
                  onClick={() => removeImage(imagePreviews.indexOf(img))}
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <button
              className="text-gray-500 hover:text-gray-700 text-sm"
              onClick={() => fileRef.current?.click()}
            >
              <i className="fa fa-image mr-1" /> Imagens
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          <button
            className="px-4 py-1.5 rounded text-sm disabled:opacity-50 transition-colors"
            style={{ backgroundColor: "var(--mantine-primary-color-filled)", color: "var(--mantine-primary-color-contrast)" }}
            onClick={handleSubmit}
            disabled={submitting}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--mantine-primary-color-filled-hover)"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--mantine-primary-color-filled)"}
          >
            {submitting ? <i className="fa fa-spinner fa-spin" /> : "Publicar"}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NewPostForm